import api from './api';

/** Matches backend list/detail shape (Prisma enums as strings). */
export interface ShipmentListItem {
  id: string;
  shipmentNumber: string;
  referenceNumber: string | null;
  type: string;
  status: string;
  priority: string;
  serviceLevel: string;
  mode: string;
  origin: { city: string; state: string; country: string };
  destination: { city: string; state: string; country: string };
  customerId: string;
  customerName: string | null;
  docketId?: string | null;
  docketNumber?: string | null;
  sourceRequisitionId?: string | null;
  company?: { id: string; name: string; email: string };
  carrier?: { id: string; name: string } | null;
  totalWeight: number;
  totalVolume: number | null;
  weightUnit: string;
  dimensionUnit: string;
  pickupDate: string | null;
  deliveryDate: string | null;
  actualDelivery: string | null;
  trackingNumber: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateShipmentApiPayload {
  referenceNumber?: string;
  type: string;
  priority?: string;
  serviceLevel?: string;
  origin: {
    street: string;
    street2?: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    latitude?: number;
    longitude?: number;
  };
  destination: {
    street: string;
    street2?: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    latitude?: number;
    longitude?: number;
  };
  customerId: string;
  customerName: string;
  customerEmail?: string;
  customerPhone?: string;
  totalWeight: number;
  totalVolume: number;
  weightUnit?: string;
  dimensionUnit?: string;
  scheduledPickupDate?: string;
  estimatedDeliveryDate?: string;
  declaredValue?: number;
  currency?: string;
  requiresSignature?: boolean;
  isHazmat?: boolean;
  isFragile?: boolean;
  specialInstructions?: string;
  docketId?: string;
  sourceRequisitionId?: string;
}

export interface PaginatedShipmentsResponse {
  data: ShipmentListItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

class ShipmentsService {
  async getAll(
    page = 1,
    pageSize = 10,
    status?: string,
    type?: string,
    customerId?: string,
  ): Promise<PaginatedShipmentsResponse> {
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('pageSize', String(pageSize));
    if (status) params.append('status', status);
    if (type) params.append('type', type);
    if (customerId) params.append('customerId', customerId);

    const response = await api.get<PaginatedShipmentsResponse>(`/shipments?${params.toString()}`);
    return response.data;
  }

  async getById(id: string): Promise<ShipmentListItem> {
    const response = await api.get<ShipmentListItem>(`/shipments/${id}`);
    return response.data;
  }

  async getTracking(shipmentId: string): Promise<unknown[]> {
    const response = await api.get<unknown[]>(`/shipments/${shipmentId}/tracking`);
    return response.data;
  }

  async create(data: CreateShipmentApiPayload): Promise<ShipmentListItem> {
    const response = await api.post<ShipmentListItem>('/shipments', data);
    return response.data;
  }

  async update(id: string, data: Record<string, unknown>): Promise<ShipmentListItem> {
    const response = await api.patch<ShipmentListItem>(`/shipments/${id}`, data);
    return response.data;
  }

  async updateStatus(id: string, status: string): Promise<ShipmentListItem> {
    const response = await api.patch<ShipmentListItem>(`/shipments/${id}/status`, { status });
    return response.data;
  }

  async delete(id: string): Promise<void> {
    await api.delete(`/shipments/${id}`);
  }
}

export default new ShipmentsService();
